import {
  WEEKDAYS,
  dayIntervals,
  parseOpeningHours,
  type Interval,
} from "../schemas/opening-hours";
import { sortServiceTimes } from "./booking-time";

/**
 * The last table a restaurant takes is this long before it closes. Owner,
 * 2026-10-07: "always accept bookings until 1 hour before closing".
 */
export const LAST_SEATING_BEFORE_CLOSE_MIN = 60;

/**
 * The venue's own lead before closing (`tablePolicy.lastSeatingBeforeCloseMin`)
 * when it is a sane number of minutes, else the house default.
 */
export function lastSeatingLeadMin(lead?: number | null): number {
  return typeof lead === "number" && Number.isInteger(lead) && lead >= 0 && lead <= 180
    ? lead
    : LAST_SEATING_BEFORE_CLOSE_MIN;
}
/** Spacing of the seatings walked from the hours when none are entered. */
export const RESTAURANT_SEATING_STEP_MIN = 30;

const MINUTES_PER_DAY = 24 * 60;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d/;

function toMinutes(time: string): number | null {
  if (!TIME_RE.test(time)) return null;
  const [h, m] = time.split(":");
  return Number(h) * 60 + Number(m);
}

function toTime(minutes: number): string {
  const m = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** The services on `date`'s weekday, [] when closed, null when unknown. */
function servicesOn(openingHours: unknown, date: string): Interval[] | null {
  const hours = parseOpeningHours(openingHours);
  if (!hours) return null;
  // Noon UTC keeps the calendar day whatever the clock of the caller.
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  if (Number.isNaN(day)) return null;
  return dayIntervals(hours[WEEKDAYS[(day + 6) % 7]!]);
}

/**
 * One service as minutes, the close pushed past 24:00 when it is after
 * midnight, and the last seating `leadMin` before it (an hour by default).
 * Null when the service is shorter than that and so takes no booking at all.
 */
function seatingWindow(service: Interval, leadMin: number): { open: number; last: number } | null {
  const open = toMinutes(service.open);
  let close = toMinutes(service.close);
  if (open === null || close === null) return null;
  if (close <= open) close += MINUTES_PER_DAY;
  const last = close - leadMin;
  return last >= open ? { open, last } : null;
}

/**
 * A restaurant's seatings on `date`, from that day's opening hours.
 *
 * One list of seatings applied to every weekday could not follow a venue whose
 * closing time moves: Trianon Casablanca closes at 00:00 Sunday to Wednesday
 * and 01:00 Thursday to Saturday, so its list had to stop at the earliest
 * close all week. The hours are per day, so the seatings are too:
 *
 * - From opening until an hour before closing (or the venue's own
 *   `lastSeatingBeforeCloseMin`), each service on its own (a
 *   lunch and a dinner service are two windows), a close after midnight
 *   belonging to the night it started on.
 * - The venue's own `time_slots`, when entered, are kept only inside those
 *   windows. They say which seatings the kitchen honours; the hours say which
 *   of them this day has. Without them the seatings are walked half-hourly.
 * - A day the venue is closed has no seatings.
 *
 * Returns null when the hours are unknown or unreadable, so the caller keeps
 * whatever it offered before. Past times are not dropped here: pass the
 * result to `filterFutureTimes`, which reads it in service order.
 */
export function restaurantSeatingsOn(
  openingHours: unknown,
  date: string,
  timeSlots?: readonly string[] | null,
  lastSeatingBeforeCloseMin?: number | null,
): string[] | null {
  const services = servicesOn(openingHours, date);
  if (services === null) return null;
  const leadMin = lastSeatingLeadMin(lastSeatingBeforeCloseMin);
  const windows = services
    .map((s) => seatingWindow(s, leadMin))
    .filter((w): w is { open: number; last: number } => w !== null);

  const fixed = (timeSlots ?? [])
    .filter((t): t is string => typeof t === "string" && t.trim().length > 0)
    .map((t) => t.trim().slice(0, 5));

  const out: string[] = [];
  if (fixed.length > 0) {
    for (const time of fixed) {
      const t = toMinutes(time);
      if (t === null) continue;
      const inside = windows.some(
        (w) =>
          (t >= w.open && t <= w.last) ||
          // 00:30 is the tail of a night that started the evening before.
          (t + MINUTES_PER_DAY >= w.open && t + MINUTES_PER_DAY <= w.last),
      );
      if (inside) out.push(time);
    }
  } else {
    for (const w of windows) {
      for (let t = w.open; t <= w.last; t += RESTAURANT_SEATING_STEP_MIN) out.push(toTime(t));
    }
  }
  return sortServiceTimes(out);
}

/**
 * The seating list a restaurant booking's time is read against, so a 00:30
 * table picked under Friday is judged and stored as Saturday's early hours.
 * The derived seatings when the hours give any, else the venue's `time_slots`
 * as before.
 */
export function restaurantServiceTimes(
  openingHours: unknown,
  date: string,
  timeSlots?: readonly string[] | null,
  lastSeatingBeforeCloseMin?: number | null,
): readonly string[] | null {
  const seatings = restaurantSeatingsOn(openingHours, date, timeSlots, lastSeatingBeforeCloseMin);
  return seatings && seatings.length > 0 ? seatings : (timeSlots ?? null);
}
